// The game's colour roles and the knob each one reads (docs/ROADMAP.md D34, docs/ART-DIRECTION.md §3). There are two
// palettes: the normal one, and the colour-blind one a player may choose (VisualSettings.colourBlind), which overrides a
// few roles and shares every other colour. The client builds its palette from these roles (render/palette.ts); the
// colour check (colourCheck.ts) judges the same roles.

import type { ColourHex } from './colour'
import type { ColourKnobId } from './knobs'
import type { TuningValues } from './values'

export const PALETTE_MODES = ['normal', 'colourBlind'] as const
export type PaletteMode = (typeof PALETTE_MODES)[number]

interface RoleKnobs {
  readonly normal: ColourKnobId
  /** The knob the colour-blind palette reads instead; absent = the same colour in both palettes. */
  readonly colourBlind?: ColourKnobId
}

const ROLE_KNOBS = {
  player: { normal: 'colour.player' },
  enemy: { normal: 'colour.enemy', colourBlind: 'colour.enemyColourBlind' },
  neutral: { normal: 'colour.neutral' },
  background: { normal: 'colour.background' },
  globeDepth: { normal: 'colour.globeDepth' },
  atmosphere: { normal: 'colour.atmosphere' },
  grid: { normal: 'colour.grid' },
  landDots: { normal: 'colour.landDots' },
  coastline: { normal: 'colour.coastline' },
  borders: { normal: 'colour.borders' },
  money: { normal: 'colour.money' },
  warning: { normal: 'colour.warning' },
  danger: { normal: 'colour.danger' },
  text: { normal: 'colour.text' },
  textDim: { normal: 'colour.textDim' },
  frame: { normal: 'colour.frame' },
  hot: { normal: 'colour.hot' },
  spark: { normal: 'colour.spark' },
} as const satisfies Readonly<Record<string, RoleKnobs>>

export type ColourRole = keyof typeof ROLE_KNOBS
export const COLOUR_ROLES = Object.keys(ROLE_KNOBS) as readonly ColourRole[]

const MODE_KNOB: Readonly<Record<PaletteMode, (knobs: RoleKnobs) => ColourKnobId>> = {
  normal: (knobs) => knobs.normal,
  colourBlind: (knobs) => knobs.colourBlind ?? knobs.normal,
}

/** The knob role `role` reads in palette `mode`. */
export function roleKnob(role: ColourRole, mode: PaletteMode): ColourKnobId {
  return MODE_KNOB[mode](ROLE_KNOBS[role])
}

/** Whether the colour-blind palette gives `role` its own colour. */
export function hasColourBlindOverride(role: ColourRole): boolean {
  const knobs: RoleKnobs = ROLE_KNOBS[role]
  return knobs.colourBlind !== undefined
}

export type PaletteColours = Readonly<Record<ColourRole, ColourHex>>

/** Every role's colour in palette `mode`. */
export function paletteColours(values: TuningValues, mode: PaletteMode): PaletteColours {
  const colours = {} as Record<ColourRole, ColourHex>
  for (const role of COLOUR_ROLES) colours[role] = values[roleKnob(role, mode)]
  return colours
}
